import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Skeleton rows for the per-day duration list, shown while the working-days
 * endpoint resolves after a date-range / category change in create mode.
 */
export const TimeOffDayListSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div className="flex flex-col gap-2">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="grid grid-cols-3 gap-2">
        <Skeleton className="h-10 rounded-md" />
        <Skeleton className="h-10 rounded-md" />
        <Skeleton className="h-10 rounded-md" />
      </div>
    ))}
  </div>
);

/** Skeleton for one labelled field (label + control). */
const FieldSkeleton = () => (
  <div className="flex flex-col gap-2">
    <Skeleton className="h-3.5 w-28" />
    <Skeleton className="h-10 w-full rounded-md" />
  </div>
);

/** Right-column balance/member card skeleton (also reused on category change). */
export const TimeOffBalanceCardSkeleton = () => (
  <Card className="flex flex-col gap-4 p-5">
    <div className="flex items-center justify-between">
      <Skeleton className="h-5 w-28" />
      <Skeleton className="h-4 w-24" />
    </div>
    <div className="flex items-center gap-2">
      <Skeleton className="h-8 w-8 rounded-md" />
      <Skeleton className="h-4 w-24" />
    </div>
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
    </div>
    <Skeleton className="h-9 w-full rounded-lg" />
  </Card>
);

/**
 * Loading skeleton for the request screen — mirrors the form card (left) + the
 * right-column card so each section fades in place instead of the page blinking.
 */
export const TimeOffRequestSkeleton = () => (
  <div className="grid w-full max-w-7xl grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
    {/* Left: form card */}
    <Card className="flex flex-col gap-6 p-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FieldSkeleton />
        <FieldSkeleton />
      </div>

      {/* Duration rows */}
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3.5 w-44" />
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="grid grid-cols-3 gap-2">
            <Skeleton className="h-10 rounded-md" />
            <Skeleton className="h-10 rounded-md" />
            <Skeleton className="h-10 rounded-md" />
          </div>
        ))}
      </div>

      {/* Approvers */}
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>

      {/* Description */}
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-24 w-full rounded-md" />
      </div>
    </Card>

    {/* Right: balance / member card */}
    <div className="lg:order-last">
      <TimeOffBalanceCardSkeleton />
    </div>
  </div>
);
